import React, { FunctionComponent, ReactNode, useMemo, useRef, useState } from 'react'
import { Button, Heading, Text, Message, Tooltip, CARET_PLACEMENTS, Link, InputAddressData, Grid } from '@afterpaytouch/core'
import { getIdentityVerifyErrorCode, IdentityErrorCode } from '@afterpaytouch/portal-api/identity'
import FormBuilder from '@afterpaytouch/form'
import { Trans, useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import styles from './style.module.css'
import { TrackingEvent } from '../../model/monitoring'
import { identityNationalIDNumber, useApplePaylaterLinkParams, useAppDispatch, useAppRouter, useConsumerSelector, useCountry } from '../../state'
import { getFormData, getSchema } from './utils'
import { IdentityVerificationForm, SchemaUpdates } from './types'
import { USER_VERIFICATION_ERRORS, US_IDV_TERMINAL_ERRORS } from '../VerifyIdentity/constants'
import { Channel, IdentityNationalIDNumberError, IdentityNationalIDNumberResponse, IdentityStatus } from '@afterpaytouch/portal-api'
import { usePostLoginRoute } from '../../config/router'
import { TermLink } from '../TermLink'
import { CloseCheckout } from '../CloseCheckout'
import { getDOBValidations } from './validations'

export const IdentityVerificationNationalNumber: FunctionComponent = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const postLoginRoute = usePostLoginRoute()
  const { headingSize, buttonSize } = useCheckoutSizes()
  const [lock, setLock] = useState(false)
  const [buttonLock, setButtonLock] = useState(false)
  const [error, setError] = useState<IdentityErrorCode | null>(null)
  const isTerminalError = useMemo(() => error && US_IDV_TERMINAL_ERRORS.includes(error), [error])
  const [showTooltip, setShowTooltip] = useState(false)
  const consumer = useConsumerSelector()
  const country = useCountry()
  const router = useAppRouter()
  const withApplePaylaterParams = useApplePaylaterLinkParams()
  const iconRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLInputElement>(null)
  const tooltipRef = useRef<HTMLInputElement>(null)
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const { profile } = consumer
  const [initialFormData] = useState(getFormData(consumer))
  const [hasSentAnalyticsData, setHasSentAnalyticsData] = useState({
    givenNames: false,
    otherGivenNames: false,
    surname: false,
    dateOfBirth: false,
    address: false,
    partialNationalIdNumber: false,
  })

  const [schemaUpdates, setSchemaUpdates] = useState<SchemaUpdates>({
    dobValidations: getDOBValidations(t, profile?.contactAddress as InputAddressData, country),
  })

  const closeTooltip = (): void => {
    setShowTooltip(false)
  }

  const toggleTooltip = (): void => {
    const newState = !showTooltip
    if (!showTooltip) {
      logEvent(TrackingEvent.PRESSED_SSN_TOOLTIP)
    }
    setShowTooltip(newState)
  }

  const renderTooltipContent = (): ReactNode => {
    return <Text size='S'>{t('verifyIdentity:us.partialNationalIdNumberTooltip')}</Text>
  }

  const onAddressInputValueChange = (): void => {
    if (!hasSentAnalyticsData.address) {
      logEvent(TrackingEvent.CHANGED_ADDRESS)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, address: true })
    }
  }

  const { schema } = useMemo(() => {
    const isUserFormError = USER_VERIFICATION_ERRORS.includes(error as IdentityErrorCode)
    const components = {
      footer: () => {
        return (
          <>
            <Text size='S'>
              <Trans
                components={{
                  privacy: <TermLink key='payment-options-privacy' color='Orca' href={withApplePaylaterParams(t('common:privacyPolicyUrl'))} />,
                  terms: <TermLink key='payment-options-terms' color='Orca' href={withApplePaylaterParams(t('common:termsOfServiceUrl'))} />,
                }}
                i18nKey='verifyIdentity:us.terms'
              />
            </Text>
            <div className={styles.button}>
              <Button.Primary
                loading={lock}
                size={buttonSize}
                type='submit'
                disabled={buttonLock}
                padding='Fluid'
                onClick={() => logEvent(TrackingEvent.PRESSED_VERIFY_IDENTITY_BUTTON)}
              >
                {t('common:continue')}
              </Button.Primary>
            </div>
          </>
        )
      },
    }

    return getSchema(consumer, country, components, t, schemaUpdates, isUserFormError, logEvent, onAddressInputValueChange, toggleTooltip, iconRef)
  }, [consumer, country, schemaUpdates, t, buttonSize, lock, buttonLock, profile, error, showTooltip, hasSentAnalyticsData])

  const identityNationalIDNumberCall = async (
    identityVerificationFormData: IdentityVerificationForm
  ): Promise<IdentityNationalIDNumberResponse | IdentityNationalIDNumberError> => {
    try {
      return await dispatch(
        identityNationalIDNumber({
          channel: Channel.CHECKOUT,
          ...identityVerificationFormData,
        })
      ).unwrap()
    } catch (e) {
      return e
    }
  }

  const handleSuccessIdentityNationalIDNumber = (response: IdentityNationalIDNumberResponse): void => {
    if (response.identityStatus === IdentityStatus.VERIFIED) {
      router.replace(postLoginRoute)
    } else if (response.identityStatus === IdentityStatus.REJECTED) {
      setLock(false)
      setButtonLock(false)
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: 'identityCheckoutFailureError' })
      setError(IdentityErrorCode.IdentityCheckRejected)
    }
  }

  const handleErrorIdentityNationalIDNumber = (errorResponse: IdentityNationalIDNumberError): void => {
    const error = getIdentityVerifyErrorCode(errorResponse)
    setLock(false)
    setError(error ?? IdentityErrorCode.Default)
  }

  const onSubmit = async (identityVerificationFormData: IdentityVerificationForm): Promise<void> => {
    setError(null)
    setLock(true)
    setButtonLock(true)

    const response = await identityNationalIDNumberCall(identityVerificationFormData)

    if ((response as IdentityNationalIDNumberResponse)?.identityStatus) {
      handleSuccessIdentityNationalIDNumber(response as IdentityNationalIDNumberResponse)
    } else {
      setButtonLock(false)
      handleErrorIdentityNationalIDNumber(response as IdentityNationalIDNumberError)
    }
  }

  const onChange = (data: IdentityVerificationForm): void => {
    setButtonLock(false)
    setError(null)

    if (data.givenNames !== initialFormData.givenNames && !hasSentAnalyticsData.givenNames) {
      logEvent(TrackingEvent.CHANGED_FIRST_NAME)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, givenNames: true })
    }
    if (data.otherGivenNames !== initialFormData.otherGivenNames && !hasSentAnalyticsData.otherGivenNames) {
      logEvent(TrackingEvent.CHANGED_MIDDLE_NAME)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, otherGivenNames: true })
    }
    if (data.surname !== initialFormData.surname && !hasSentAnalyticsData.surname) {
      logEvent(TrackingEvent.CHANGED_LAST_NAME)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, surname: true })
    }
    if (data.dateOfBirth !== initialFormData.dateOfBirth && !hasSentAnalyticsData.dateOfBirth) {
      logEvent(TrackingEvent.CHANGED_DOB)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, dateOfBirth: true })
    }
    if (data.partialNationalIdNumber.length === 4 && !hasSentAnalyticsData.partialNationalIdNumber) {
      logEvent(TrackingEvent.SSN_FORM_FIELD_ENTERED)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, partialNationalIdNumber: true })
    }

    const { address } = data
    const dobValidations = getDOBValidations(t, address, country)

    setSchemaUpdates((currSchema) => ({
      ...currSchema,
      dobValidations,
    }))
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.halfScreen}>
        {isTerminalError && (
          <>
            <Heading size={headingSize}>{t(`verifyIdentity:us.errorHeading`)}</Heading>
            <div className={styles.profileInformationMessage}>
              <Text>{t('verifyIdentity:us.terminalErrorBody')}</Text>
            </div>
            <div className={styles.backButton}>
              <Grid>
                <Grid.Item>
                  <CloseCheckout kind='Secondary' padding='Fluid'>
                    {t('terminalError:backToStore')}
                  </CloseCheckout>
                </Grid.Item>
              </Grid>
            </div>
          </>
        )}
        {error && !isTerminalError && error !== IdentityErrorCode.DuplicateIdentity && (
          <>
            <Heading size={headingSize}>{t(`verifyIdentity:us.errorHeading`)}</Heading>
            <div className={styles.profileInformationMessage}>
              <Text size='S'>
                <Trans
                  components={{
                    tryAgain: (
                      <Link kind='Underline' onClick={() => setError(null)}>
                        {t('verifyIdentity:us.tryAgain')}
                      </Link>
                    ),
                  }}
                  i18nKey='verifyIdentity:us.recoverableErrorBody'
                />
              </Text>
            </div>
            <div className={styles.backButton}>
              <Grid>
                <Grid.Item>
                  <Button
                    kind='Ghost'
                    padding='Fluid'
                    onClick={() => {
                      setError(null)
                    }}
                  >
                    {t('verifyIdentity:us.tryAgainCta')}
                  </Button>
                </Grid.Item>
              </Grid>
            </div>
          </>
        )}
        {(!error || error === IdentityErrorCode.DuplicateIdentity) && !isTerminalError && (
          <>
            <Heading size={headingSize}>{t(`verifyIdentity:us.title`)}</Heading>
            <div className={styles.profileInformationMessage}>
              <Text>{t('verifyIdentity:us.description')}</Text>
            </div>
            {error === IdentityErrorCode.DuplicateIdentity && (
              <div className={styles.verificationFailed}>
                <Message
                  kind='Error'
                  iconName='Profile'
                  heading={t(`consumerLending:identityVerification.duplicateIdentityErrorLabels.title`)}
                  description={t(`consumerLending:identityVerification.duplicateIdentityErrorLabels.message`)}
                />
              </div>
            )}
            <FormBuilder schema={schema} testNameSpace={'identity-verification'} onSubmit={onSubmit} onChange={onChange} disabled={lock} />
            {showTooltip && (
              <div className={styles.tooltipContainer}>
                <Tooltip
                  id='partial-national-number-id-tooltip'
                  triggerRef={iconRef}
                  hasCaret={true}
                  caretPlacement={CARET_PLACEMENTS.TOP}
                  testNameSpace='payment-types-pcl-tooltip'
                  content={renderTooltipContent()}
                  usePortal={false}
                  bubbleSize={{ width: '80%' }}
                  ref={tooltipRef}
                  onClose={closeTooltip}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
